import React, { useEffect, useState } from "react";
import API_BASE, { IS_SANDBOX } from "../config";

// ── EventsPage ─────────────────────────────────────────────────────
// Dashboard showing all projects, their status, and EN 12193 results.
// Fetches from /events endpoint — read-only, no calculations triggered.
// Auto-refreshes every 30 seconds to show live progress.

const EventsPage = ({ onBack }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch events from the backend
  const fetchEvents = async () => {
    try {
      const res = await fetch(`${API_BASE}/events`);
      const data = await res.json();
      setEvents(data);
    } catch (err) {
      console.error("Failed to fetch events", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
    // Auto-refresh every 30 seconds so we see new calculations appear
    const interval = setInterval(fetchEvents, 30000);
    return () => clearInterval(interval);
  }, []);

  // Helper: render a pass/fail badge
  const Badge = ({ pass }) => (
    <span style={{
      display: "inline-block",
      width: 20,
      height: 20,
      borderRadius: "50%",
      backgroundColor: pass ? "#22c55e" : "#ef4444",
      color: "#fff",
      fontSize: 12,
      lineHeight: "20px",
      textAlign: "center",
      fontWeight: "bold",
    }}>
      {pass ? "\u2713" : "\u2717"}
    </span>
  );

  // Helper: status badge colour
  const statusColor = (status) => {
    if (status === "completed") return "#22c55e";
    if (status === "pending") return "#f59e0b";
    return "#94a3b8";
  };

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: "#f1f5f9", zIndex: 10000, overflow: "auto",
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      {/* Header bar */}
      <div style={{
        backgroundColor: "#1e293b", color: "#fff", padding: "16px 24px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <button onClick={onBack} style={{
            background: "none", border: "1px solid #64748b", color: "#fff",
            padding: "6px 16px", borderRadius: 6, cursor: "pointer", fontSize: 14,
          }}>
            &larr; Back to App
          </button>
          <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>
            ILS Sport — Events Dashboard
          </h1>
        </div>
        <div style={{ fontSize: 13, color: "#94a3b8" }}>
          {events.length} project{events.length !== 1 ? "s" : ""} &middot; Auto-refreshes every 30s
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: "24px", maxWidth: 1400, margin: "0 auto" }}>
        {loading ? (
          <p style={{ textAlign: "center", color: "#64748b", marginTop: 60 }}>Loading...</p>
        ) : events.length === 0 ? (
          <p style={{ textAlign: "center", color: "#64748b", marginTop: 60 }}>No projects yet.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{
              width: "100%", borderCollapse: "collapse", backgroundColor: "#fff",
              borderRadius: 8, overflow: "hidden",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}>
              <thead>
                <tr style={{ backgroundColor: "#1e293b", color: "#fff", fontSize: 13 }}>
                  <th style={thStyle}>ID</th>
                  <th style={thStyle}>Project</th>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Field (m)</th>
                  <th style={thStyle}>Mast H</th>
                  <th style={thStyle}>Luminaires</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>E<sub>m</sub> (lux)</th>
                  <th style={thStyle}>U<sub>0</sub></th>
                  <th style={thStyle}>R<sub>G</sub></th>
                  <th style={thStyle}>TA/PA Illum</th>
                  <th style={thStyle}>TA/PA Unif</th>
                  <th style={thStyle}>Ev<sub>max</sub></th>
                  <th style={thStyle}>Ks<sub>max</sub></th>
                  <th style={thStyle}>EN 12193</th>
                  {IS_SANDBOX && <th style={thStyle}>Fitness</th>}
                  {IS_SANDBOX && <th style={thStyle}>Calc Time</th>}
                  <th style={thStyle}>Report</th>
                </tr>
              </thead>
              <tbody>
                {events.map((ev) => (
                  <tr key={ev.id} style={{ borderBottom: "1px solid #e2e8f0" }}>
                    <td style={tdStyle}>{ev.id}</td>
                    {/* Project name + town column.
                        Long strings are clipped with CSS ellipsis (max-width 220px)
                        and the full value is shown via the title attribute on hover.
                        This avoids having to rename individual projects in the DB when
                        users enter overly long names. */}
                    <td style={{ ...tdStyle, fontWeight: 500, maxWidth: 220 }}>
                      <div
                        title={ev.project_name || ""}
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {ev.project_name || "—"}
                      </div>
                      {ev.project_town && (
                        <div
                          title={ev.project_town}
                          style={{
                            fontSize: 11,
                            color: "#94a3b8",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {ev.project_town}
                        </div>
                      )}
                    </td>
                    <td style={{ ...tdStyle, fontSize: 12, color: "#64748b" }}>
                      {ev.project_creation_date || "—"}
                    </td>
                    <td style={tdStyle}>
                      {ev.field_length && ev.field_width
                        ? `${ev.field_length} x ${ev.field_width}`
                        : "—"}
                    </td>
                    {/* Mast height from first lightpoint */}
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      {ev.mast_height ? `${ev.mast_height}m` : "—"}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>{ev.luminaire_count || "—"}</td>
                    <td style={tdStyle}>
                      <span style={{
                        display: "inline-block", padding: "2px 10px", borderRadius: 12,
                        fontSize: 12, fontWeight: 600, color: "#fff",
                        backgroundColor: statusColor(ev.status),
                      }}>
                        {ev.status}
                      </span>
                    </td>

                    {/* EN 12193 result columns — only show if results exist */}
                    {ev.results ? (
                      <>
                        <td style={tdStyle}>
                          {ev.results.pa_ehave} <Badge pass={ev.criteria_met.em} />
                        </td>
                        <td style={tdStyle}>
                          {ev.results.pa_u} <Badge pass={ev.criteria_met.uniformity} />
                        </td>
                        <td style={tdStyle}>
                          {ev.results.rg} <Badge pass={ev.criteria_met.rg} />
                        </td>
                        <td style={tdStyle}>
                          {Math.round(ev.results.ta_to_pa_ehave * 100)}% <Badge pass={ev.criteria_met.ta_pa_illum} />
                        </td>
                        <td style={tdStyle}>
                          {Math.round(ev.results.ta_to_pa_u * 100)}% <Badge pass={ev.criteria_met.ta_pa_unif} />
                        </td>
                        <td style={tdStyle}>
                          {ev.results.evmax} <Badge pass={ev.criteria_met.evmax} />
                        </td>
                        <td style={tdStyle}>
                          {ev.results.ksmax} <Badge pass={ev.criteria_met.ksmax} />
                        </td>
                        <td style={{ ...tdStyle, textAlign: "center" }}>
                          {ev.all_criteria_met ? (
                            <span style={{ color: "#22c55e", fontWeight: 700, fontSize: 16 }}>PASS</span>
                          ) : (
                            <span style={{ color: "#ef4444", fontWeight: 700, fontSize: 16 }}>FAIL</span>
                          )}
                        </td>
                        {/* Fitness and Calc Time — sandbox only */}
                        {IS_SANDBOX && (
                          <td style={{
                            ...tdStyle, textAlign: "center", fontWeight: 700,
                            color: ev.fitness >= 700 ? "#22c55e" : ev.fitness >= 500 ? "#f59e0b" : "#ef4444",
                          }}>
                            {ev.fitness != null ? `${ev.fitness} / 700` : "—"}
                          </td>
                        )}
                        {IS_SANDBOX && (
                          <td style={{ ...tdStyle, textAlign: "center", fontSize: 12, color: "#64748b" }}>
                            {ev.calculation_time_seconds != null
                              ? `${Math.floor(ev.calculation_time_seconds / 60)}:${String(Math.round(ev.calculation_time_seconds % 60)).padStart(2, "0")}`
                              : "—"}
                          </td>
                        )}
                        {/* Print Report button — sends to lightmap-report */}
                        <td style={{ ...tdStyle, textAlign: "center" }}>
                          <button
                            onClick={async () => {
                              try {
                                const res = await fetch(`${API_BASE}/printreport/${ev.id}`, { method: "POST" });
                                if (!res.ok) throw new Error("Report failed");
                                const data = await res.json();
                                window.open(data.url, "_blank");
                              } catch (e) {
                                alert("Failed to generate report");
                              }
                            }}
                            style={{
                              padding: "4px 10px", fontSize: 11, borderRadius: 4,
                              border: "1px solid #3b82f6", background: "#eff6ff",
                              color: "#3b82f6", cursor: "pointer", fontWeight: 600,
                            }}
                          >
                            Report
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        {/* Empty cells for projects without results */}
                        <td style={tdStyle} colSpan={12}>
                          <span style={{ color: "#94a3b8", fontStyle: "italic" }}>
                            {ev.status === "pending" ? "Calculating..." : "No results"}
                          </span>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Table cell styles ──────────────────────────────────────────────
const thStyle = {
  padding: "6px 8px",
  textAlign: "left",
  fontWeight: 600,
  whiteSpace: "nowrap",
  fontSize: 12,
};

const tdStyle = {
  padding: "6px 8px",
  fontSize: 12,
  whiteSpace: "nowrap",
};

export default EventsPage;
